'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import {
  ArrowLeft,
  Heart,
  Share2,
  Star,
  Users,
  Cog,
  Luggage,
  Fuel,
  Gauge,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  Sparkles,
  Info,
  ChevronRight,
  Phone
} from 'lucide-react';
import { Vehicle } from '@/types';
import { useCurrency } from '@/components/site/CurrencyContext';
import { useLocale } from '@/lib/i18n';
import { CarIllustration } from './CarIllustrations';
import { RENTAL_LOCATIONS } from '@/lib/data';

interface VehicleDetailPageProps {
  vehicle: Vehicle;
  allVehicles: Vehicle[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
  onBackToFleet: () => void;
  onBookNow: (vehicle: Vehicle) => void;
  onSelectSimilarVehicle: (vehicle: Vehicle) => void;
}

export function VehicleDetailPage({
  vehicle,
  allVehicles,
  favorites,
  onToggleFavorite,
  onBackToFleet,
  onBookNow,
  onSelectSimilarVehicle,
}: VehicleDetailPageProps) {
  const { fmt, equiv } = useCurrency();
  const { t } = useLocale();
  // Gallery active image
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Reservation widget state
  const [pickupDate, setPickupDate] = useState('2026-10-18');
  const [dropoffDate, setDropoffDate] = useState('2026-10-22');
  const [pickupLocation, setPickupLocation] = useState('Miami International Airport (MIA)');

  const isFav = favorites.includes(vehicle.id);

  const images = vehicle.galleryImages && vehicle.galleryImages.length > 0
    ? vehicle.galleryImages
    : vehicle.imageUrl
    ? [vehicle.imageUrl]
    : [];

  const activeImage = images[activeImageIndex] || vehicle.imageUrl;

  // Calculate rental duration
  const start = new Date(pickupDate).getTime();
  const end = new Date(dropoffDate).getTime();
  const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
  const subtotal = vehicle.pricePerDay * days;

  // Similar vehicles (same category or similar price, excluding current)
  const similarVehicles = allVehicles
    .filter((v) => v.id !== vehicle.id)
    .slice(0, 3);

  const handleShare = () => {
    navigator.clipboard?.writeText?.(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="w-full bg-slate-50/60 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* 1. Breadcrumbs & Top Navigation Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBackToFleet}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 transition-colors cursor-pointer shadow-xs"
            >
              <ArrowLeft className="w-4 h-4 text-[var(--color-primary)]" />
              <span>{t('det.back')}</span>
            </button>

            <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400">
              <span>{t('det.home')}</span>
              <span>/</span>
              <span>{t('det.vehicles')}</span>
              <span>/</span>
              <span className="text-slate-600">{vehicle.category}</span>
              <span>/</span>
              <span className="text-[var(--color-primary)] font-semibold">{vehicle.name}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer shadow-xs"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>{copiedLink ? t('det.copied') : t('det.share')}</span>
            </button>

            <button
              type="button"
              onClick={() => onToggleFavorite(vehicle.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 bg-white border rounded-xl text-xs font-semibold transition-colors cursor-pointer shadow-xs ${
                isFav
                  ? 'border-rose-200 text-rose-600 bg-rose-50/50'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${isFav ? 'fill-rose-500 text-rose-500' : ''}`} />
              <span>{isFav ? t('det.saved') : t('det.save')}</span>
            </button>
          </div>
        </div>

        {/* 2. Main Title Banner */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 text-[var(--color-primary)] border border-amber-500/20 text-[10px] font-black uppercase tracking-wider">
                {vehicle.category} {t('det.class')}
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">
                {vehicle.year} {t('det.model')}
              </span>
              <div className="flex items-center gap-1 text-xs font-bold text-amber-500">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span>{vehicle.rating}</span>
                <span className="text-slate-400 font-normal">({vehicle.reviewsCount} {t('det.reviews')})</span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              {vehicle.name}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              {vehicle.brand} {vehicle.model} · {vehicle.colorName} {t('det.exterior')}
              {vehicle.licensePlate ? (
                <>
                  {' '}· {t('det.license')}: <span className="font-mono font-bold text-slate-700">{vehicle.licensePlate}</span>
                </>
              ) : null}
            </p>
          </div>

          <div className="text-left sm:text-right">
            <span className="text-xs text-slate-400 block font-medium">{t('det.rate')}</span>
            <span className="text-3xl font-black text-[var(--color-primary)] tracking-tight font-mono">
              {fmt(vehicle.pricePerDay)}
            </span>
            <span className="text-xs text-slate-400 font-medium"> / day</span>
            {equiv(vehicle.pricePerDay) ? (
              <div className="text-[11px] text-slate-400">{equiv(vehicle.pricePerDay)}</div>
            ) : null}
          </div>
        </div>

        {/* 3. Main 2-Column Split: Detailed Specs & Sticky Reservation Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Gallery, Specs, Amenities, Policies (Span 8) */}
          <div className="lg:col-span-8 space-y-8">
            {/* Main Showcase Gallery */}
            <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-6 shadow-xs">
              <div className="relative w-full h-[320px] sm:h-[420px] rounded-2xl overflow-hidden bg-slate-50 flex items-center justify-center">
                {!imageError && activeImage ? (
                  <Image
                    src={activeImage}
                    alt={vehicle.name}
                    fill
                    priority
                    className="object-cover object-center transition-all duration-300"
                    referrerPolicy="no-referrer"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <CarIllustration type={vehicle.imageType} className="w-full h-auto max-h-64 object-contain p-4" />
                )}

                {/* Status indicator on image */}
                <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-lg bg-black/60 backdrop-blur-md border border-white/10 text-white text-xs font-bold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>{t('det.available')}</span>
                </div>
              </div>

              {/* Thumbnails row */}
              {images.length > 1 && (
                <div className="flex items-center gap-3 mt-4 overflow-x-auto no-scrollbar pb-1">
                  {images.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`relative w-24 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer shrink-0 ${
                        activeImageIndex === idx
                          ? 'border-[var(--color-primary)] ring-2 ring-amber-500/20'
                          : 'border-slate-200 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <Image
                        src={imgUrl}
                        alt={`${vehicle.name} angle ${idx + 1}`}
                        fill
                        className="object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">{t('det.capacity')}</span>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-900">{vehicle.seats} {t('det.seatsU')}</span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                  <Cog className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">{t('det.transmission')}</span>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-900">{vehicle.transmission}</span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                  <Luggage className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">{t('det.luggage')}</span>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-900">{vehicle.bags} {t('det.bagsU')}</span>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-[var(--color-primary)] flex items-center justify-center shrink-0">
                  <Fuel className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">{t('det.powertrain')}</span>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-900">{vehicle.fuelType}</span>
                </div>
              </div>
            </div>

            {/* Vehicle Narrative & Specifications */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-6">
              <div>
                <h2 className="text-lg font-extrabold text-slate-900 tracking-tight mb-2">
                  {t('det.overview')}
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  {t('det.narrA')} <strong>{vehicle.name}</strong> {t('det.narrB')} {vehicle.category.toLowerCase()} {t('det.narrC')} ({vehicle.mpg}) {t('det.narrD')}
                </p>
              </div>

              {/* Technical Specifications Grid */}
              <div className="pt-6 border-t border-slate-100">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-4">
                  {t('det.specs')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.engine')}</span>
                    <span className="font-semibold text-slate-900">{vehicle.fuelType} Turbo</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.economy')}</span>
                    <span className="font-semibold text-slate-900">{vehicle.mpg}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.mileage')}</span>
                    <span className="font-semibold text-slate-900 font-mono">{vehicle.mileage.toLocaleString()} km</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.seating')}</span>
                    <span className="font-semibold text-slate-900">{vehicle.seats} {t('det.fullSeats')}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.drive')}</span>
                    <span className="font-semibold text-slate-900">{t('det.driveV')}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500">{t('det.airbag')}</span>
                    <span className="font-semibold text-emerald-600 font-bold">5-Star NHTSA</span>
                  </div>
                </div>
              </div>

              {/* Included Amenities List */}
              <div className="pt-6 border-t border-slate-100">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-4">
                  {t('det.features')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-700">
                  {vehicle.features.map((feat, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>{t('det.unlimited')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>{t('det.clean')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span>{t('det.fuel')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Rental Policy & Requirements */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs space-y-4">
              <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                {t('det.req')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-slate-600">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                  <strong className="block text-slate-900 font-bold mb-1">{t('det.age')}</strong>
                  <span>{t('det.ageD')}</span>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                  <strong className="block text-slate-900 font-bold mb-1">{t('det.deposit')}</strong>
                  <span>{t('det.depositD')}</span>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                  <strong className="block text-slate-900 font-bold mb-1">{t('det.cancel')}</strong>
                  <span>{t('det.cancelD')}</span>
                </div>
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                  <strong className="block text-slate-900 font-bold mb-1">{t('det.fuelP')}</strong>
                  <span>{t('det.fuelPD')}</span>
                </div>
              </div>
            </div>

            {/* Verified Customer Reviews */}
            <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-xs">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900 tracking-tight">
                    {t('det.revT')}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t('det.outOf')} {vehicle.rating} / 5 — {t('det.revSub')}
                  </p>
                </div>
                <div className="text-2xl font-black text-[var(--color-primary)] font-mono">
                  {vehicle.rating} / 5
                </div>
              </div>

              <div className="space-y-4 text-xs">
                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-extrabold text-slate-900">Yassine E. — {t('det.verified')}</span>
                    <div className="flex text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    &ldquo;{t('det.rev1')}&rdquo;
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-extrabold text-slate-900">Salma B. — {t('det.verified')}</span>
                    <div className="flex text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed">
                    &ldquo;{t('det.rev2')}&rdquo;
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Quick Reservation Calculator Card (Span 4) */}
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xl space-y-5">
              <div className="flex justify-between items-baseline border-b border-slate-100 pb-4">
                <div>
                  <span className="text-3xl font-black text-[var(--color-primary)] font-mono tracking-tight">
                    {fmt(vehicle.pricePerDay)}
                  </span>
                  <span className="text-xs text-slate-400 font-semibold"> / day</span>
                  {equiv(vehicle.pricePerDay) ? (
                    <div className="text-[11px] text-slate-400">{equiv(vehicle.pricePerDay)}</div>
                  ) : null}
                </div>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  {t('det.best')}
                </span>
              </div>

              {/* Form Selectors */}
              <div className="space-y-3 text-xs">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">{t('det.pickLoc')}</label>
                  <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                    <MapPin className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                    <select
                      value={pickupLocation}
                      onChange={(e) => setPickupLocation(e.target.value)}
                      className="w-full bg-transparent font-semibold text-slate-900 focus:outline-hidden cursor-pointer"
                    >
                      {RENTAL_LOCATIONS.map((loc) => (
                        <option key={loc.id} value={`${loc.name} (${loc.code})`}>
                          {loc.name} ({loc.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">{t('det.pickDate')}</label>
                    <input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="font-bold text-slate-700 block mb-1">{t('det.retDate')}</label>
                    <input
                      type="date"
                      value={dropoffDate}
                      onChange={(e) => setDropoffDate(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-semibold text-slate-900"
                    />
                  </div>
                </div>
              </div>

              {/* Itemized Calculation */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-2 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>{fmt(vehicle.pricePerDay)} × {days} {t('det.days')}</span>
                  <span className="font-mono font-semibold">{fmt(subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>{t('det.unlimM')}</span>
                  <span className="text-emerald-600 font-bold">{t('det.free')}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>{t('det.liab')}</span>
                  <span className="text-emerald-600 font-bold">{t('det.included')}</span>
                </div>
                <div className="pt-2 border-t border-slate-200 flex justify-between items-baseline font-bold text-slate-900">
                  <span>{t('det.total')}</span>
                  <span className="text-xl font-black text-[var(--color-primary)] font-mono">{fmt(subtotal)}</span>
                </div>
              </div>

              {/* Big Vibrant Reserve Button */}
              <button
                type="button"
                onClick={() => onBookNow(vehicle)}
                className="w-full py-4 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-orange-950/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>{t('det.reserve')} {vehicle.name.toUpperCase()}</span>
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Trust signals */}
              <div className="space-y-2 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t('det.cancelHr')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>{t('det.noCharge')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[var(--color-primary)] shrink-0" />
                  <span>{t('det.roadside')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Similar Vehicles Recommendations Section */}
        {similarVehicles.length > 0 && (
          <div className="mt-16 pt-12 border-t border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="text-xs font-black tracking-wider uppercase text-[var(--color-primary)] block mb-1">
                  {t('det.more')}
                </span>
                <h3 className="text-xl font-extrabold text-slate-900 tracking-tight">
                  {t('det.similar')}
                </h3>
              </div>
              <button
                type="button"
                onClick={onBackToFleet}
                className="text-xs font-bold text-[var(--color-primary)] hover:underline cursor-pointer"
              >
                {t('det.viewFleet')} →
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {similarVehicles.map((simCar) => (
                <div
                  key={simCar.id}
                  onClick={() => onSelectSimilarVehicle(simCar)}
                  className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs hover:shadow-lg transition-all cursor-pointer group flex flex-col justify-between"
                >
                  <div>
                    <div className="relative w-full h-36 rounded-xl overflow-hidden bg-slate-50 mb-3">
                      {simCar.imageUrl ? (
                        <Image
                          src={simCar.imageUrl}
                          alt={simCar.name}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-500"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <CarIllustration type={simCar.imageType} className="w-full h-auto p-2" />
                      )}
                    </div>

                    <div className="text-[10px] font-bold text-slate-400 uppercase">{simCar.category}</div>
                    <h4 className="text-base font-extrabold text-slate-900 group-hover:text-[var(--color-primary)] transition-colors">
                      {simCar.name}
                    </h4>
                    <p className="text-xs text-slate-500 mt-1">
                      {simCar.seats} {t('det.seatsU')} · {simCar.transmission} · {simCar.mpg}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-lg font-black text-[var(--color-primary)] font-mono">
                      {fmt(simCar.pricePerDay)} <span className="text-xs font-normal text-slate-400">/ day</span>
                    </span>
                    <span className="text-xs font-bold text-slate-700 group-hover:text-[var(--color-primary)]">
                      {t('det.viewDetails')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
